import { useCallback } from 'react';
import BackendService from 'BackendService';
import { CrossDeviceAuthData, PairingStatus } from './types';

export interface PairingActions {
  startPairing: () => Promise<void>;
  handleApprove: () => Promise<void>;
  handleDeny: () => Promise<void>;
}

export function usePairingActions(
  authData: CrossDeviceAuthData | null,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  setAuthData: (data: CrossDeviceAuthData | null) => void,
  setPairingStatus: (status: PairingStatus) => void,
  setShowApproval: (show: boolean) => void,
  setCorrectChoice: (choice: string) => void,
): PairingActions {
  const startPairing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setShowApproval(false);
      setCorrectChoice('');
      setPairingStatus('pending');

      const backendService = new BackendService();
      const response = await backendService.startPairingSession('web', {
        platform: 'web',
        userAgent: navigator.userAgent,
      });

      setAuthData({
        pairingId: response.pairingId,
        expiresAt: response.expiresAt,
        ticketUrl: `${window.location.origin}/auth/pair?pairingId=${response.pairingId}`,
        qrData: response.qrData, // Include the backend-generated QR data
      });
    } catch (err: any) {
      console.error('Failed to start pairing:', err);
      setError(err.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setAuthData, setPairingStatus, setShowApproval, setCorrectChoice]);

  const handleApprove = useCallback(async () => {
    if (!authData) return;

    try {
      const backendService = new BackendService();
      const response = await backendService.approvePairing(authData.pairingId, {
        platform: 'web',
        userAgent: navigator.userAgent,
      });

      // Set the correct choice from the backend response
      setCorrectChoice(response.correctChoice);

      console.log('Pairing approved successfully, correctChoice:', response.correctChoice);
    } catch (error) {
      console.error('Failed to approve pairing:', error);
      setError('Failed to approve login. Please try again.');
    }
  }, [authData, setError, setCorrectChoice]);

  const handleDeny = useCallback(async () => {
    if (!authData) return;

    try {
      // Since there's no deny endpoint, we'll just reset to generate new QR
      console.log('User denied pairing, generating new QR code');
      await startPairing();
    } catch (error) {
      console.error('Failed to reset pairing:', error);
      setError('Failed to reset pairing. Please refresh to try again.');
    }
  }, [authData, setError, startPairing]);

  return { startPairing, handleApprove, handleDeny };
}
